import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Flame, TrendingUp, Trophy, Target, FileText, Sparkles, Link2 } from 'lucide-react'
import { api } from '../../lib/api'
import {
  HomeShell,
  HomeHeader,
  HomeQuickActions,
  HomeAlertStrip,
  HomePrimaryGrid,
  HomeSecondaryRow,
  HomeFabDock,
} from '../../components/home'
import XPBar from '../../components/student/XPBar'
import OnboardingModal from '../../components/student/OnboardingModal'
import StreakDisplay from '../../components/student/StreakDisplay'
import ChallengeList from '../../components/student/ChallengeList'
import RecentAchievements from '../../components/student/RecentAchievements'
import { DailyProgress } from '../../components/student/DailyProgress'
import { ScanWorkflow } from '../../components/student/ScanWorkflow'
import { FridayFolder } from '../../components/student/FridayFolder'
import { StudentQuickScan } from '../../components/student/StudentQuickScan'
import { useEngagement } from '../../hooks/useEngagement'

interface DashboardData {
  student: { id: number; name: string }
  profile: {
    level: number
    title: string
    total_xp: number
    xp_in_level: number
    xp_for_next: number
    progress_pct: number
    xp_this_week: number
  }
  streaks: {
    study_streak: number
    longest_study_streak: number
    assignment_streak: number
    streak_shields: number
  }
  challenges: Array<{
    id: number
    title: string
    description: string
    type: string
    xp_reward: number
    progress: number
    deadline: string | null
  }>
  recent_achievements: Array<{
    id: number
    code: string
    name: string
    category: string
    tier: string
    earned_at: string
  }>
  weak_skills_count: number
  uncategorized_scans_count: number
  stats: {
    total_achievements: number
    total_challenges_completed: number
  }
  unmatched_scans_count?: number
  sync: {
    last_synced: string | null
    minutes_ago: number | null
    is_stale: boolean
  }
}

export default function StudentDashboard() {
  const engagement = useEngagement()

  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['student-dashboard'],
    queryFn: async () => {
      const response = await api.get('/api/student/dashboard')
      return response.data
    },
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-32 bg-surface-card rounded-xl"></div>
        <div className="grid grid-cols-3 gap-6">
          <div className="h-24 bg-surface-card rounded-xl"></div>
          <div className="h-24 bg-surface-card rounded-xl"></div>
          <div className="h-24 bg-surface-card rounded-xl"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-400">Failed to load dashboard</p>
        <button
          onClick={() => window.location.reload()}
          className="btn btn-primary mt-4"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!data) return null

  return (
    <HomeShell>
      <OnboardingModal />

      <HomeHeader
        title={`Welcome back, ${data.student.name}!`}
        subtitle={`Level ${data.profile.level} ${data.profile.title}`}
        actions={<div className="level-badge text-2xl">{data.profile.level}</div>}
      />

      <HomeQuickActions
        actions={[
          {
            id: 'match-scans',
            label: 'Match Scans',
            subtitle: `${data.unmatched_scans_count ?? 0} pending`,
            icon: Link2,
            to: '/student/scans?tab=match',
            iconClassName: 'bg-quest-primary/20 text-quest-primary',
          },
          {
            id: 'my-scans',
            label: 'Scan Homework',
            subtitle: 'Open scan history and tools',
            icon: FileText,
            to: '/student/scans',
            iconClassName: 'bg-quest-accent/20 text-quest-accent',
          },
        ]}
      />

      {/* Science Test Prep Games */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/student/arcade?play=sequence-order" className="block group">
          <div className="rounded-xl p-[2px]" style={{
            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
          }}>
            <div className="rounded-[10px] bg-surface-card p-4 flex flex-col items-center text-center gap-2 transition-colors group-hover:bg-surface-hover min-h-[100px] justify-center">
              <span className="text-3xl">🪨</span>
              <p className="font-bold">Rocks</p>
              <p className="text-xs text-gray-400">Rock layers &amp; cycles</p>
            </div>
          </div>
        </Link>
        <Link to="/student/arcade?play=definition-match" className="block group">
          <div className="rounded-xl p-[2px]" style={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          }}>
            <div className="rounded-[10px] bg-surface-card p-4 flex flex-col items-center text-center gap-2 transition-colors group-hover:bg-surface-hover min-h-[100px] justify-center">
              <span className="text-3xl">🔬</span>
              <p className="font-bold">Science Vocab</p>
              <p className="text-xs text-gray-400">Match terms &amp; definitions</p>
            </div>
          </div>
        </Link>
      </div>


      <DailyProgress
        activeSeconds={engagement.activeSeconds}
        progressPct={engagement.progressPct}
        completedMinimum={engagement.completedMinimum}
        remainingSeconds={engagement.remainingSeconds}
        currentStreak={engagement.currentStreak}
        multiplier={engagement.multiplier}
        notifications={engagement.notifications}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-orange-900/50 flex items-center justify-center">
            <Flame className="w-6 h-6 text-orange-500 animate-fire" />
          </div>
          <div>
            <p className="text-2xl font-bold">{data.streaks.study_streak}</p>
            <p className="text-sm text-gray-400">Day Streak</p>
          </div>
        </div>

        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-green-900/50 flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-green-500" />
          </div>
          <div>
            <p className="text-2xl font-bold">+{data.profile.xp_this_week}</p>
            <p className="text-sm text-gray-400">XP This Week</p>
          </div>
        </div>

        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-purple-900/50 flex items-center justify-center">
            <Trophy className="w-6 h-6 text-purple-500" />
          </div>
          <div>
            <p className="text-2xl font-bold">{data.stats.total_achievements}</p>
            <p className="text-sm text-gray-400">Achievements</p>
          </div>
        </div>

        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-900/50 flex items-center justify-center">
            <Target className="w-6 h-6 text-blue-500" />
          </div>
          <div>
            <p className="text-2xl font-bold">{data.stats.total_challenges_completed}</p>
            <p className="text-sm text-gray-400">Challenges Done</p>
          </div>
        </div>
      </div>

      {(data.uncategorized_scans_count > 0 || data.weak_skills_count > 0) && (
        <HomeAlertStrip>
          {data.uncategorized_scans_count > 0 && (
            <Link to="/student/scans?uncategorized=true" className="block">
              <div className="card border-quest-accent/50 bg-quest-accent/10 hover:bg-quest-accent/20 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-quest-accent/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-quest-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-quest-accent flex items-center gap-2">
                        {data.uncategorized_scans_count} scan{data.uncategorized_scans_count > 1 ? 's' : ''} need categorization
                        <Sparkles className="w-4 h-4" />
                      </p>
                      <p className="text-sm text-gray-400">
                        Add missing info to earn up to +{data.uncategorized_scans_count * 10} XP
                      </p>
                    </div>
                  </div>
                  <div className="text-quest-accent text-sm font-medium">View &rarr;</div>
                </div>
              </div>
            </Link>
          )}

          {data.weak_skills_count > 0 && (
            <div className="card border-orange-500/50 bg-orange-900/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <span className="text-xl">!</span>
                </div>
                <div>
                  <p className="font-medium text-orange-300">
                    {data.weak_skills_count} skill{data.weak_skills_count > 1 ? 's' : ''} need attention
                  </p>
                  <p className="text-sm text-gray-400">
                    Check your Skills page for recovery challenges
                  </p>
                </div>
              </div>
            </div>
          )}
        </HomeAlertStrip>
      )}

      <HomePrimaryGrid>
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-xl font-bold mb-4">XP Progress</h2>
            <XPBar
              currentXP={data.profile.xp_in_level}
              maxXP={data.profile.xp_for_next}
              totalXP={data.profile.total_xp}
              level={data.profile.level}
            />
          </div>

          <div className="card">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-quest-primary" />
              Today's Challenges
            </h2>
            <ChallengeList challenges={data.challenges} />
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              Streak Status
            </h2>
            <StreakDisplay
              studyStreak={data.streaks.study_streak}
              longestStreak={data.streaks.longest_study_streak}
              assignmentStreak={data.streaks.assignment_streak}
              shields={
                engagement.multiplier > 1
                  ? Math.floor((engagement.multiplier - 1) * 10)
                  : data.streaks.streak_shields
              }
            />
          </div>

          <div className="card">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-quest-accent" />
              Recent Achievements
            </h2>
            <RecentAchievements achievements={data.recent_achievements} />
          </div>
        </div>
      </HomePrimaryGrid>

      <HomeSecondaryRow>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ScanWorkflow />
          <FridayFolder />
        </div>
      </HomeSecondaryRow>

      <HomeFabDock>
        <StudentQuickScan />
      </HomeFabDock>
    </HomeShell>
  )
}
